import React, { useState } from "react";

import { Pagination } from "@performyard/py-components";
import type { SortChangedEvent } from "@performyard/py-components";
import { ASYNC_TASK_NAMES } from "@performyard/service-contracts";
import type {
	CompetencyAssignmentJSON,
	CompetencyAssignmentUserDict,
	CompetencyDict,
	RoleDict,
} from "@performyard/service-contracts";

import DownloadButtonSSE from "../../../../../components.smart/DownloadButtonSSE";
import FilterChips from "../../../../../components/FilterChips";
import PageHeading from "../../../../../components/PageHeading";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import CompetenciesFilterPanel from "../../../components/CompetenciesFilterPanel";
import { type CompetenciesFilter } from "../../../components/CompetenciesFilterPanel/schema";
import {
	convertFiltersToFilterChips,
	getActiveFiltersCount,
	getNonDismissibleChipIds,
	removeFilter,
} from "../../../components/CompetenciesFilterPanel/utils";
import CompetenciesTable from "../../../components/CompetenciesTable";
import UpdateCurrentLevelModal from "../../../components/UpdateCurrentLevelModal";
import ViewHistoryPanel from "../../../components/ViewHistoryPanel";
import ViewLevelsPanel from "../../../components/ViewLevelsPanel";

import { useTeamCompetencyAssignments } from "../../../../../api/hooks";
import { useIsNewNavigation } from "../../../../../api/hooks/defs/navigation";
import { useUserAsyncTask } from "../../../../../hooks/useUserAsyncTask";
import useCompetencyAssignmentActions from "../../../hooks/useCompetencyAssignmentActions";

import { requestCompetencyAssignmentsCsv } from "../../../../../api/services";
import type { ListTeamCompetencyAssignmentsParams } from "../../../../../api/services";

import { PAGE_SIZE, colIdToSortBy, competencySortFromEvent } from "../../../utils";

import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";

const PAGE_TITLE = "My Team's Competencies";

type MyTeamsCompetenciesProps = {
	employeeId: string;
	employeeName?: string | null;
	navLinks: SideNavigationLink[];
	activePage: string;
	canUpdateCurrentLevel: boolean;
	canExportCsv: boolean;
};

const MyTeamsCompetencies = ({
	employeeId,
	employeeName,
	navLinks,
	activePage,
	canUpdateCurrentLevel,
	canExportCsv,
}: MyTeamsCompetenciesProps): React.ReactElement => {
	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<CompetenciesFilter>({
		defaultSortName: "assignee_name",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
	});

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
	const isNewNav = useIsNewNavigation();

	const params: ListTeamCompetencyAssignmentsParams = {
		employeeId,
		sortBy: colIdToSortBy(sort.name) ?? "assignee_name",
		sortOrder: sort.direction,
		status: filters.status,
		source: filters.source,
		levelGap: filters.levelGap,
		assigneeId: filters.assignee?.id,
		competencyId: filters.competency?.id,
		roleIds: filters.roles?.map((role) => role.id),
		search: debouncedSearchValue || undefined,
		limit: PAGE_SIZE,
		skip: (pageNumber - 1) * PAGE_SIZE,
	};

	const { data, isLoading } = useTeamCompetencyAssignments(params, {
		placeholderData: (previousData) => previousData,
	});

	const { task: exportTask, triggerTask: triggerExport } = useUserAsyncTask(
		ASYNC_TASK_NAMES.EXPORT_COMPETENCY_ASSIGNMENTS_CSV
	);

	const assignments: CompetencyAssignmentJSON[] = data?.assignments ?? [];
	const competencies: CompetencyDict = data?.competencies ?? {};
	const users: CompetencyAssignmentUserDict = data?.users ?? {};
	const roles: RoleDict = data?.roles ?? {};
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / PAGE_SIZE);

	// Competency filter options come from the dedicated ACTIVE-only field — the `competencies` dict
	// is status-scoped for row resolution and can carry archived entries under archived/any views.
	// Sorted by name.
	const competencyOptions = Object.entries(data?.competencyOptions ?? {})
		.map(([id, c]) => ({ _id: id, name: c.name }))
		.sort((a, b) => a.name.localeCompare(b.name));

	// Role filter options = the team's distinct own-roles (stable set, independent of status).
	const roleOptions = Object.entries(roles)
		.map(([id, r]) => ({ _id: id, name: r.name }))
		.sort((a, b) => a.name.localeCompare(b.name));

	const {
		openPanel,
		actionSelectedRow,
		isUpdateModalOpen,
		onOpenPanel,
		closePanel,
		onUpdateLevel,
		closeUpdateModal,
	} = useCompetencyAssignmentActions();

	const activeCompetency = actionSelectedRow ? competencies[actionSelectedRow.competency_id] : undefined;
	// Team panels show the assignee in their header card (Figma); My Competencies passes none.
	const activeAssignee = actionSelectedRow
		? { _id: actionSelectedRow.user_id, name: users[actionSelectedRow.user_id]?.name ?? "" }
		: undefined;

	const updateCompetency = actionSelectedRow ? competencies[actionSelectedRow.competency_id] : undefined;

	const onSortChanged = (event: SortChangedEvent<CompetencyAssignmentJSON>) => {
		const nextSort = competencySortFromEvent(event);
		if (nextSort) setSort(nextSort);
	};

	const mainContent = (
		<div data-testid="my-teams-competencies">
			<div className="flex">
				<CompetenciesTable
					assignments={assignments}
					competencies={competencies}
					users={users}
					loading={isLoading}
					canUpdateCurrentLevel={canUpdateCurrentLevel}
					sort={sort}
					onSortChanged={onSortChanged}
					onOpenPanel={onOpenPanel}
					onUpdateLevel={onUpdateLevel}
				/>
			</div>
			{pageCount > 1 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}
		</div>
	);

	const filterChips = convertFiltersToFilterChips(filters);

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(v) => setDebouncedSearch(v)}
			searchPlaceholder="Search competencies..."
			onFilterButtonPress={() => setIsFiltersPanelOpen(true)}
			activeFiltersCount={getActiveFiltersCount(filters)}
		/>
	);

	const drawer = (
		<CompetenciesFilterPanel
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			variant="team"
			competencyOptions={competencyOptions}
			roleOptions={roleOptions}
			onApplyFilters={(f) => setFilters(f)}
		/>
	);

	const handleDeleteFilter = (id: string) => {
		const nextFilters = removeFilter(filters, id);
		if (nextFilters) setFilters(nextFilters);
	};

	const toolbarInformationRow = (
		<FilterChips
			filters={filterChips}
			label="Filters applied: "
			nonDismissibleFilters={getNonDismissibleChipIds(filters)}
			onDeleteFilter={handleDeleteFilter}
		/>
	);

	const actionButtons = canExportCsv
		? [
				<DownloadButtonSSE
					key="download-csv"
					task={exportTask}
					onClick={() =>
						triggerExport(() =>
							requestCompetencyAssignmentsCsv({
								scope: "team",
								employeeId,
								search: debouncedSearchValue || undefined,
								status: filters.status,
								source: filters.source,
								levelGap: filters.levelGap,
								assigneeId: filters.assignee?.id,
								competencyIds: filters.competency?.id ? [filters.competency.id] : undefined,
								roleIds: filters.roles?.map((role) => role.id),
								sortBy: colIdToSortBy(sort.name) ?? "assignee_name",
								sortOrder: sort.direction,
							})
						)
					}
					testid="download-csv"
				>
					Download CSV
				</DownloadButtonSSE>,
			]
		: [];

	const assignmentId = actionSelectedRow?._id ?? "";
	return (
		<>
			<InfoPageTemplate
				employeeID={employeeId}
				pageTitle={employeeName ? `Competencies - ${employeeName} - PerformYard` : "Team Competencies"}
				pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
				navigationLinks={navLinks}
				activeNavigationLinkKey={activePage}
				actionButtons={actionButtons}
				toolbarActionRow={toolbarActionRow}
				drawer={drawer}
				toolbarInformationRow={toolbarInformationRow}
				mainContent={mainContent}
			/>
			<ViewLevelsPanel
				key={`levels-${assignmentId}`}
				isOpen={openPanel === "levels"}
				onClose={closePanel}
				competency={activeCompetency}
				assignee={activeAssignee}
				currentLevel={actionSelectedRow?.current_level ?? null}
				expectedLevel={actionSelectedRow?.expected_level ?? null}
			/>
			<ViewHistoryPanel
				key={`history-${assignmentId}`}
				isOpen={openPanel === "history"}
				onClose={closePanel}
				assignmentId={assignmentId}
				competency={activeCompetency}
				assignee={activeAssignee}
			/>
			<UpdateCurrentLevelModal
				isOpen={isUpdateModalOpen}
				onClose={closeUpdateModal}
				assignment={actionSelectedRow}
				competency={updateCompetency}
				assignee={activeAssignee}
			/>
		</>
	);
};

export default MyTeamsCompetencies;
